import { useEffect, useState } from 'react'
import { L10N } from 'global/localization'
import { LayoutType } from 'global/types/enums'
import {
  appendNotifications,
  clearNotifications,
  notificationsSelector,
  setCurrentPage,
  setNotifications,
  useGetNotificationsQuery
} from 'store/notifications'
import { Layout } from 'shared/components/layout'
import { DataHandler } from 'shared/components/data-handler'
import { Empty } from 'shared/components/empty'
import { NotificationContent } from './content'
import { useDispatch, useSelector } from 'react-redux'

export const Notifications = () => {
  const [refreshing, setRefreshing] = useState<boolean>(false)
  const { currentPage, notifications: allNotifications } = useSelector(notificationsSelector)
  const dispatch = useDispatch()

  const [pageSize] = useState<number>(10)

  const {
    data: notificationsResponse,
    isLoading,
    error,
    refetch
  } = useGetNotificationsQuery({
    start: currentPage * pageSize,
    length: pageSize
  })

  const notifications = notificationsResponse?.data || []
  const totalRecords = notificationsResponse?.recordsTotal || 0
  const hasMorePages = (currentPage + 1) * pageSize < totalRecords

  const handleRefresh = async () => {
    setRefreshing(true)
    dispatch(clearNotifications())
    try {
      await refetch()
    } finally {
      setRefreshing(false)
    }
  }

  const handleLoadMore = () => {
    if (hasMorePages && !isLoading) {
      dispatch(setCurrentPage(currentPage + 1))
    }
  }

  // Update all notifications when new data is loaded
  useEffect(() => {
    if (notifications.length > 0) {
      if (currentPage === 0) {
        // First page - replace all notifications
        dispatch(setNotifications(notifications))
      } else {
        // Subsequent pages - append notifications
        dispatch(appendNotifications(notifications))
      }
    }
  }, [notifications, currentPage])

  useEffect(() => {
    document.title = [L10N.LABEL.NOTIFICATIONS || 'Notifications', 'Bluelink Subscription'].join(
      ' - '
    )
  }, [])

  return (
    <Layout
      noPadding
      title={L10N.LABEL.NOTIFICATIONS || 'Notifications'}
      type={LayoutType.Header}
      actionIcon={refreshing ? 'spinner' : 'refresh'}
      onAction={handleRefresh}
    >
      <DataHandler
        isLoading={isLoading && currentPage === 0}
        error={error}
        onReload={handleRefresh}
      >
        {allNotifications && allNotifications.length > 0 ? (
          <div>
            <NotificationContent notifications={allNotifications} />
            {hasMorePages && (
              <div className='p-4'>
                <button
                  onClick={handleLoadMore}
                  className='w-full py-3 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50'
                  disabled={isLoading}
                >
                  {isLoading
                    ? L10N.LABEL.LOADING || 'Loading...'
                    : L10N.LABEL.LOAD_MORE || 'Load More'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className='flex justify-center items-center h-full w-full'>
            <Empty />
          </div>
        )}
      </DataHandler>
    </Layout>
  )
}
